// TaxBreakdown is defined canonically in ../utils/vat and re-exported through
// the package barrel; import it here so this module can reference the type
// without redeclaring it (which would make the barrel export ambiguous).
import type { TaxBreakdown } from '../utils/vat';
import type { OperatingHours } from '../utils/operatingHours';
import { tUi } from '../i18n';

// User and Authentication Types
export interface User {
  id: string;
  email: string | null;
  phone: string | null;
  name: string | null;
  is_superadmin: boolean;
  locale: string;
  garage_memberships?: GarageMembership[];
  created_at: string;
  updated_at: string;
}

export interface GarageMembership {
  id: string;
  user_id: string;
  garage_id: string;
  role: 'owner' | 'manager' | 'mechanic';
  permissions: Record<string, boolean>;
  active: boolean;
  garage?: Garage;
  created_at: string;
  updated_at: string;
}

export type UserRole = 'superadmin' | 'user' | 'owner' | 'manager' | 'mechanic';

// Garage Types
export interface Garage {
  id: string;
  name: string;
  address: string | null;
  kommun: string;
  latitude: number | null;
  longitude: number | null;
  rating: number;
  review_count: number;
  email: string | null;
  phone: string | null;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Vehicle Types
export interface Vehicle {
  id: string;
  user_id: string;
  reg_number: string;
  make: string | null;
  model: string | null;
  year: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Inspection Request Types
export type InspectionRequestStatus = 'open' | 'closed' | 'booked';

/** Optional add-on the customer asked to have quoted alongside the inspection. */
export interface ExtraService {
  /** One of ServiceCategory. */
  category: string;
  note?: string | null;
}

export interface InspectionRequest {
  id: string;
  user_id: string;
  vehicle_id: string;
  status: InspectionRequestStatus;
  description: string | null;
  kommun: string | null;
  latitude: number | null;
  longitude: number | null;
  street_address: string | null;
  quote_id: string | null;
  vehicle?: Vehicle;
  inspection_issues?: InspectionIssue[];
  extra_services?: ExtraService[];
  quotes?: Quote[];
  deletable?: boolean;
  /** True when the customer has cancelled (soft-deleted) the request. */
  cancelled?: boolean;
  cancelled_at?: string | null;
  has_inspection_report?: boolean;
  inspection_report_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface InspectionIssue {
  id: string;
  inspection_request_id: string;
  title: string;
  description: string | null;
  severity: string | null;
  created_at: string;
  updated_at: string;
}

// Service Types
export type ServiceCategory =
  | 'general'
  | 'oil'
  | 'tires'
  | 'brakes'
  | 'engine'
  | 'electrical'
  | 'ac'
  | 'bodywork'
  | 'inspection'
  | 'diagnostics'
  | 'other';

export const SERVICE_CATEGORIES: { value: ServiceCategory; label: string }[] = [
  { value: 'general', get label() { return tUi('serviceCategory.general'); } },
  { value: 'oil', get label() { return tUi('serviceCategory.oil'); } },
  { value: 'tires', get label() { return tUi('serviceCategory.tires'); } },
  { value: 'brakes', get label() { return tUi('serviceCategory.brakes'); } },
  { value: 'engine', get label() { return tUi('serviceCategory.engine'); } },
  { value: 'electrical', get label() { return tUi('serviceCategory.electrical'); } },
  { value: 'ac', get label() { return tUi('serviceCategory.ac'); } },
  { value: 'bodywork', get label() { return tUi('serviceCategory.bodywork'); } },
  { value: 'inspection', get label() { return tUi('serviceCategory.inspection'); } },
  { value: 'diagnostics', get label() { return tUi('serviceCategory.diagnostics'); } },
  { value: 'other', get label() { return tUi('serviceCategory.other'); } },
];

export type CarCareServiceCategory =
  | 'exterior_wash'
  | 'interior_cleaning'
  | 'full_detail'
  | 'wax_polish'
  | 'tire_rim'
  | 'window_cleaning'
  | 'engine_cleaning'
  | 'other';

export const CAR_CARE_SERVICE_CATEGORIES: { value: CarCareServiceCategory; label: string }[] = [
  { value: 'exterior_wash', get label() { return tUi('carCareCategory.exterior_wash'); } },
  { value: 'interior_cleaning', get label() { return tUi('carCareCategory.interior_cleaning'); } },
  { value: 'full_detail', get label() { return tUi('carCareCategory.full_detail'); } },
  { value: 'wax_polish', get label() { return tUi('carCareCategory.wax_polish'); } },
  { value: 'tire_rim', get label() { return tUi('carCareCategory.tire_rim'); } },
  { value: 'window_cleaning', get label() { return tUi('carCareCategory.window_cleaning'); } },
  { value: 'engine_cleaning', get label() { return tUi('carCareCategory.engine_cleaning'); } },
  { value: 'other', get label() { return tUi('carCareCategory.other'); } },
];

export interface Service {
  id: string;
  garage_id: string;
  name: string;
  description: string | null;
  price: number | null;
  category: ServiceCategory;
  duration_minutes: number | null;
  active: boolean;
  image_url?: string | null;
  has_image?: boolean;
  created_at: string;
  updated_at: string;
}

// Quote Types
export type QuoteStatus = 'pending' | 'accepted' | 'rejected';

export interface QuoteLineItem {
  id: string;
  /** Null on rows that hang off a service booking instead of a quote. */
  quote_id: string | null;
  booking_id?: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  unit_type: string;
  position: number;
  created_at: string;
  updated_at: string;
  amount_excl_vat?: number;
  vat_amount?: number;
  amount_incl_vat?: number;
}

export interface Quote {
  id: string;
  inspection_request_id: string;
  deletable?: boolean;
  /** Garage-side: work order still open (job not finished/cancelled). */
  editable?: boolean;
  garage_id: string;
  price: number;
  message: string | null;
  status: QuoteStatus;
  proposed_time_slots: string[];
  garage?: Garage;
  inspection_request?: InspectionRequest;
  line_items?: QuoteLineItem[];
  calculated_total?: number;
  price_includes_vat?: boolean;
  vat_rate?: number;
  tax_breakdown?: TaxBreakdown;
  created_at: string;
  updated_at: string;
}

// Booking Types
export type BookingStatus = 'pending_payment' | 'confirmed' | 'completed' | 'cancelled';
export type CarCareBookingStatus = 'pending_payment' | 'pending' | 'confirmed' | 'completed' | 'cancelled';

type CancellationReason = {
  value:
    | 'no_show'
    | 'customer_cancelled'
    | 'vehicle_not_delivered'
    | 'technical_problem'
    | 'staff_illness'
    | 'weather'
    | 'other';
  readonly label: string;
};

export const CANCELLATION_REASONS: CancellationReason[] = [
  { value: 'no_show', get label() { return tUi('cancellationReason.no_show'); } },
  { value: 'customer_cancelled', get label() { return tUi('cancellationReason.customer_cancelled'); } },
  { value: 'vehicle_not_delivered', get label() { return tUi('cancellationReason.vehicle_not_delivered'); } },
  { value: 'technical_problem', get label() { return tUi('cancellationReason.technical_problem'); } },
  { value: 'staff_illness', get label() { return tUi('cancellationReason.staff_illness'); } },
  { value: 'weather', get label() { return tUi('cancellationReason.weather'); } },
  { value: 'other', get label() { return tUi('cancellationReason.other'); } },
];

export type CancellationReasonValue = typeof CANCELLATION_REASONS[number]['value'];

export interface Booking {
  id: string;
  user_id: string;
  garage_id: string;
  quote_id: string;
  scheduled_at: string | null;
  status: BookingStatus;
  payment_status?: string;
  /** How the customer is paying — Klarna online, or in person at the garage. */
  payment_method?: 'klarna' | 'pay_at_garage';
  booking_type?: 'service' | 'inspection';
  title?: string;
  work_notes?: string | null;
  cancellation_reason?: string | null;
  photo_urls?: string[];
  /** Efterkontroll (re-inspection errand): null until the garage offers it. */
  aftercheck_status?: 'offered' | 'approved' | 'declined' | 'completed' | null;
  aftercheck_price?: number | null;
  aftercheck_completed_at?: string | null;
  /** True when the job is finished and an offer can (still) be made. */
  aftercheck_offerable?: boolean;
  /** The garage's configured fee, shown before an offer is made. */
  garage_aftercheck_price?: number | null;
  /** What the job is billed at — re-stamped when the work order changes. */
  amount_incl_vat?: number | null;
  /** The job's work order: the quote's rows, or the booking's own. */
  line_items?: QuoteLineItem[];
  /** False once the job is completed or cancelled. */
  work_order_editable?: boolean;
  user?: User;
  garage?: Garage;
  quote?: Quote;
  service?: Service;
  /** Only on staff-created service jobs; quote jobs reach the car via the request. */
  vehicle?: { id: string; reg_number: string; make: string | null; model: string | null; year: number | null } | null;
  created_at: string;
  updated_at: string;
}

export interface CarCareBooking {
  id: string;
  /** Set after successful payment */
  booking_reference: string | null;
  car_care_center_id: string;
  car_care_service_id: string;
  car_care_service: { id: string; name: string; duration_minutes: number };
  scheduled_at: string;
  status: CarCareBookingStatus;
  payment_status: string;
  /** How the customer is paying — Klarna online, or in person at the center. */
  payment_method?: 'klarna' | 'pay_at_garage';
  price: number;
  notes: string | null;
  work_notes: string | null;
  cancellation_reason: string | null;
  photo_urls: string[];
  user?: { id: string; name: string | null; phone: string | null; email: string | null };
  vehicle?: { id: string; reg_number: string; make: string | null; model: string | null; year: number | null } | null;
  created_at: string;
  updated_at: string;
}

// Review Types
export interface Review {
  id: number;
  rating: number;
  comment: string | null;
  /** Business reply to the review (shown to customers too). */
  reply: string | null;
  replied_at: string | null;
  /** Display name of the reviewer. */
  user_name: string | null;
  created_at: string;
}

export interface ReviewsResponse {
  reviews: Review[];
  average_rating: number;
  review_count: number;
  current_page: number;
  has_more: boolean;
}

// Payment Types
export interface PaymentMethod {
  id: string;
  user_id: string;
  stripe_customer_id: string | null;
  stripe_payment_method_id: string | null;
  last4: string | null;
  brand: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

// Dashboard Types
export interface RevenueMonthPoint {
  month: string;
  revenue: number;
}

export interface WeekQuotePoint {
  day: string;
  quotes: number;
}

export interface WeekCountPoint {
  day: string;
  count: number;
}

export interface DashboardActivityItem {
  id: string;
  type: 'request' | 'quote' | 'booking' | 'car_care_booking' | 'review';
  /** Numeric id of the underlying record (request/quote/booking) */
  entity_id?: number;
  /** For quote items: the inspection request the quote belongs to */
  request_id?: number;
  title: string;
  description: string;
  created_at: string;
}

export interface OnboardingStep {
  key: string;
  required: boolean;
  done: boolean;
}

export interface Onboarding {
  steps: OnboardingStep[];
  completed_at: string | null;
}

export interface GarageDashboardStats {
  total_requests: number;
  pending_requests: number;
  total_quotes: number;
  accepted_quotes: number;
  quote_conversion_rate: number;
  total_bookings: number;
  upcoming_bookings: number;
  monthly_revenue: number;
  average_response_time: number;
  revenue_by_month: RevenueMonthPoint[];
  quotes_this_week_by_day: WeekQuotePoint[];
  recent_activity: DashboardActivityItem[];
  onboarding: Onboarding;
}

export interface CarCareDashboardStats {
  total_bookings: number;
  pending_bookings: number;
  upcoming_bookings: number;
  completed_bookings: number;
  total_revenue: number;
  average_rating: number;
  revenue_by_month: RevenueMonthPoint[];
  bookings_this_week_by_day: WeekCountPoint[];
  recent_activity: DashboardActivityItem[];
  onboarding: Onboarding;
}

/** @deprecated Use GarageDashboardStats */
export type DashboardStats = GarageDashboardStats;

/** @deprecated Use DashboardActivityItem */
export type ActivityItem = DashboardActivityItem;

// API Response Types
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

export interface ApiError {
  success: false;
  error: string;
  message: string;
  details?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
}

// Form Types
export interface LoginFormData {
  phone?: string;
  email?: string;
}

export interface OTPFormData {
  code: string;
}

export interface QuoteLineItemFormData {
  description: string;
  quantity: number;
  unit_price: number;
  unit_type: string;
}

export interface QuoteFormData {
  price: number;
  message: string;
  proposed_time_slots: string[];
  line_items?: QuoteLineItemFormData[];
}

export interface ServiceFormData {
  name: string;
  description: string;
  price: number | null;
  category: ServiceCategory;
  duration_minutes: number | null;
  active: boolean;
  image?: File | null;
}

export interface BookingFormData {
  scheduled_at: string;
}

export interface ReviewFormData {
  rating: number;
  comment: string;
}

export interface StaffInvitationFormData {
  email?: string;
  phone?: string;
  role: 'manager' | 'mechanic';
}

export interface GarageProfileFormData {
  name: string;
  address: string;
  kommun: string;
  email: string;
  phone: string;
  description: string;
  services: string[];
  operating_hours: Record<string, { open: string; close: string }>;
}

type AdTemplate = {
  key: 'brand' | 'navy' | 'sunset' | 'forest' | 'slate' | 'minimal';
  readonly name: string;
  gradient: [string, string];
  textColor: string;
  accentBg: string;
  accentText: string;
};

export const AD_TEMPLATES: AdTemplate[] = [
  {
    key: 'brand',
    get name() { return tUi('adTemplate.brand'); },
    gradient: ['#3B4D8D', '#2d3a6b'],
    textColor: '#ffffff',
    accentBg: '#F5D547',
    accentText: '#1a1a1a',
  },
  {
    key: 'navy',
    get name() { return tUi('adTemplate.navy'); },
    gradient: ['#1e3a5f', '#2d5a8e'],
    textColor: '#ffffff',
    accentBg: '#93c5fd',
    accentText: '#1e3a5f',
  },
  {
    key: 'sunset',
    get name() { return tUi('adTemplate.sunset'); },
    gradient: ['#f97316', '#dc2626'],
    textColor: '#ffffff',
    accentBg: '#fef3c7',
    accentText: '#92400e',
  },
  {
    key: 'forest',
    get name() { return tUi('adTemplate.forest'); },
    gradient: ['#14532d', '#166534'],
    textColor: '#ffffff',
    accentBg: '#86efac',
    accentText: '#14532d',
  },
  {
    key: 'slate',
    get name() { return tUi('adTemplate.slate'); },
    gradient: ['#0f172a', '#334155'],
    textColor: '#ffffff',
    accentBg: '#cbd5e1',
    accentText: '#0f172a',
  },
  {
    key: 'minimal',
    get name() { return tUi('adTemplate.minimal'); },
    gradient: ['#f8fafc', '#e2e8f0'],
    textColor: '#0f172a',
    accentBg: '#3B4D8D',
    accentText: '#ffffff',
  },
];

export type AdTemplateKey = typeof AD_TEMPLATES[number]['key'];

export interface Ad {
  id: number;
  title: string;
  description?: string | null;
  template_key: AdTemplateKey;
  cta_text?: string | null;
  cta_url?: string | null;
  target_kommun?: string | null;
  start_date: string;
  end_date: string;
  active: boolean;
  priority: number;
}

/** Garage or car-care-center profile returned by the dashboard profile endpoint. */
export interface BusinessProfile {
  id: number | string;
  name: string;
  address: string | null;
  kommun: string;
  email: string | null;
  phone: string | null;
  description: string | null;
  website?: string | null;
  operating_hours?: OperatingHours;
  rating: number;
  review_count: number;
  active?: boolean;
  image_url?: string | null;
  logo_url?: string | null;
  /** What the garage charges for driving the car to the re-inspection. */
  aftercheck_price?: number;
  /** How far ahead customers may book. null = inherit the platform default. */
  booking_horizon_days?: number | null;
  /** Read-only: what `booking_horizon_days: null` resolves to. */
  default_booking_horizon_days?: number;
}
